import { FC, memo, useEffect, useMemo, useState } from 'react';
import { RadioGroup, Form } from 'rsuite';
import { RadioCell } from 'components/Rsuite/Radio';

import {
  BidMode,
  RoasStrategy,
  ManualKeywordType,
  MANUAL_UNIQUE_KEY,
  SuggestKeywordType,
} from 'core/rtbCampaign/singlePage/keyword';

import { ManualKeyword } from './ManualKeyword/ManualKeyword';
import { AutoKeyword } from './AutoKeyword';
import { useCampaignStore } from 'store/campaignStore';

import { useBidRange } from '../../Hooks/useBidRange';
import { ActionType, SelectPattern } from 'core/rtbCampaign/singlePage/common';
import Skeleton from 'react-loading-skeleton';
import { RsInput, RsInputSize } from 'components/Rsuite/Input';

export interface BidTypeSelectionProps {
  selectPattern: SelectPattern;
  bidMode: BidMode;
  onBidTypeChange: (s: BidMode) => void;
  roasStrategy: RoasStrategy;
  roas?: string;
  onRoasStrategyChange: (v: RoasStrategy) => void;
  onCustomRoasChange: (v: string) => void;
  manualKeywords: ManualKeywordType[];
  onManualKeywordsChange: (data: ManualKeywordType[]) => void;
  suggestKeywords: SuggestKeywordType[];
  manualError?: string;
  keywordErrors?: Record<string, any>[];
  roasError?: string;
}

export const BidTypeSelection: FC<BidTypeSelectionProps> = memo(
  ({
    bidMode,
    onBidTypeChange,
    roasStrategy,
    roas,
    onRoasStrategyChange,
    onCustomRoasChange,
    manualKeywords,
    onManualKeywordsChange,
    suggestKeywords,
    manualError,
    keywordErrors = [],
    roasError,
  }) => {
    const bidRange = useBidRange();
    const { actionType, roasList } = useCampaignStore();
    const [loading, setLoading] = useState(true);

    const handleBidTypeChange = (v) => {
      onBidTypeChange(v);
    };

    const handleAutoTypeChange = (v) => {
      onRoasStrategyChange(v);
    };

    const handleCustomRoasChange = (v: string) => {
      onCustomRoasChange(v);
    };

    const customRoasMAx = () => {
      const baseValue = roasList[1];
      if (baseValue === '-') return 50;
      const computed =
        Math.round((Number(baseValue) + Number.EPSILON) * 1.5 * 100) / 100;
      return computed > 50 ? 50 : computed;
    };

    const faultyKeywordsMap = useMemo(
      () =>
        keywordErrors.reduce((acc, cur, i) => {
          if (cur) {
            acc[manualKeywords[i]?.[MANUAL_UNIQUE_KEY]] = cur;
          }
          return acc;
        }, {}),
      [manualKeywords, keywordErrors],
    );

    const faultyKeywords = useMemo(
      () => Object.keys(faultyKeywordsMap),
      [faultyKeywordsMap],
    );

    useEffect(() => {
      const timer = setTimeout(() => setLoading(false), 4000);
      return () => clearTimeout(timer);
    }, []);

    return (
      <RadioGroup value={bidMode} onChange={handleBidTypeChange}>
        <RadioCell
          value={BidMode.AUTO}
          label='自動出價與選字'
          disabled={
            actionType === ActionType.EDIT ||
            (actionType === ActionType.CREATE &&
              suggestKeywords.length === 0) ||
            (actionType === ActionType.COPY && suggestKeywords.length === 0)
          }
          customBottom={
            (actionType === ActionType.CREATE &&
              suggestKeywords.length === 0) ||
            (actionType === ActionType.COPY && suggestKeywords.length === 0) ? (
              <Form.HelpText>
                您選擇的商品未有推薦關鍵字，請使用手動出價與選字
              </Form.HelpText>
            ) : (
              bidMode === BidMode.AUTO && (
                <AutoKeyword
                  data={[
                    {
                      value: RoasStrategy.HIGH_ROAS,
                      label: loading ? (
                        <Skeleton width={450} />
                      ) : (
                        <span className='text-[#717273]'>
                          <span className='mr-3 text-[#242328]'>
                            投入產出比設定為
                            <span className='mx-1 font-bold text-[#635DA3]'>
                              {roasList[0]}
                            </span>
                            以上
                          </span>
                          曝光導向，轉換表現為輔
                        </span>
                      ),
                    },
                    {
                      value: RoasStrategy.MEDIUM_ROAS,
                      label: loading ? (
                        <Skeleton width={450} />
                      ) : (
                        <span className='text-[#717273]'>
                          <span className='mr-3 text-[#242328]'>
                            投入產出比設定為
                            <span className='mx-1 font-bold text-[#635DA3]'>
                              {roasList[1]}
                            </span>
                            以上
                          </span>
                          兼顧曝光與轉換，穩定投遞
                        </span>
                      ),
                    },
                    {
                      value: RoasStrategy.LOW_ROAS,
                      label: loading ? (
                        <Skeleton width={450} />
                      ) : (
                        <span className='text-[#717273]'>
                          <span className='mr-3 text-[#242328]'>
                            投入產出比設定為
                            <span className='mx-1 font-bold text-[#635DA3]'>
                              {roasList[2]}
                            </span>
                            以上
                          </span>
                          成效導向，出價保守投遞易受限
                        </span>
                      ),
                    },
                    {
                      value: RoasStrategy.CUSTOM_ROAS,
                      label: loading ? (
                        <Skeleton width={450} />
                      ) : (
                        <span className='text-[#717273]'>
                          <span className='mr-3 flex items-center text-[#242328]'>
                            自訂投入產出比設定為{' '}
                            <div className='float-none mx-2 inline-block'>
                              <RsInput
                                size={RsInputSize.SMALL}
                                className='max-w-[86px]'
                                placeholder={`1 ~ ${customRoasMAx()}`}
                                value={roas ?? ''}
                                onChange={handleCustomRoasChange}
                                disabled={
                                  roasStrategy !== RoasStrategy.CUSTOM_ROAS
                                }
                                errorMessage={roasError}
                              />
                            </div>{' '}
                            以上
                          </span>
                        </span>
                      ),
                    },
                  ]}
                  autoMode={roasStrategy}
                  onChange={handleAutoTypeChange}
                />
              )
            )
          }
        />
        <RadioCell
          value={BidMode.MANUAL}
          label='手動出價與選字'
          disabled={actionType === ActionType.EDIT}
          customBottom={
            bidMode === BidMode.MANUAL && (
              <div className='relative'>
                <ManualKeyword
                  suggestKeywords={suggestKeywords}
                  data={manualKeywords}
                  onChange={onManualKeywordsChange}
                  keywordErrorsMap={faultyKeywordsMap}
                />
                <Form.ErrorMessage
                  show={manualError !== undefined}
                  placement='bottomStart'
                >
                  {manualError}
                </Form.ErrorMessage>
                <Form.ErrorMessage
                  show={faultyKeywords.length !== 0}
                  placement='leftStart'
                >
                  <div className='max-h-[360px] w-max max-w-[240px] overflow-auto text-wrap'>
                    <p className='font-bold'>
                      出價範圍須為 0 ~ {bidRange[1]} 元 :
                    </p>
                    {faultyKeywords.join(' / ')}
                  </div>
                </Form.ErrorMessage>
              </div>
            )
          }
        />
      </RadioGroup>
    );
  },
);
